import ContextMenu from "components/layout/MagicEditor/ContextMenu";
import React, { useCallback, useEffect, useRef, useState } from "react";

const ContextMenuContext = React.createContext({
    isOpen: false,
    openContextMenu: (payload) => void 0,
    closeContextMenu: () => void 0,
    toggleContextMenu: (payload) => void 0,
    contextMenuRef: undefined,
});

const ContextMenuProvider = ({ children }) => {

    const [ network, setNetwork ] = useState("");
    const [ value, setValue ] = useState("");
    const [ onChange, setOnChange ] = useState({ handler: () => void 0 });
    const [ menuOpened, setMenuOpened ] = useState(false);
    const [ position, setPosition ] = useState({ x: 0, y: 0 });
    const [ fullText, setFullText ] = useState("");
    const [ selectedText, setSelectedText ] = useState("");
    const [ selectionStartEnd, setSelectionStartEnd ] = useState({ start: 0, finish: 0 });

    const inputRef = useRef();
    const contextMenuRef = useRef();
    // Track the live value so applyValue can resolve function-updaters
    // (AddEmojis / AddLink) against the latest text even across rapid clicks.
    const valueRef = useRef(value);
    useEffect(() => { valueRef.current = value; }, [value]);
    // Guard so the very gesture that opens the menu can't immediately close it.
    const openedAtRef = useRef(0);

    const openContextMenu = (payload = {}) => {
        setNetwork(payload.network);
        setValue(payload.value);
        valueRef.current = payload.value;
        setOnChange({ handler: payload.onChange });
        setPosition(payload.position || { x: 0, y: 0 });
        setFullText(payload.fullText);
        setSelectedText(payload.selectedText);
        setSelectionStartEnd(payload.selectionStartEnd || { start: 0, finish: 0 });
        openedAtRef.current = Date.now();
        setMenuOpened(true);
    };

    const closeContextMenu = useCallback(() => setMenuOpened(false), []);

    const toggleContextMenu = (payload) => {
        if (menuOpened) closeContextMenu();
        else openContextMenu(payload);
    };

    // The setter the ContextMenu functions use. Only a real AI action calls it,
    // so merely opening the menu never mutates the editor (no false "dirty").
    const applyValue = useCallback((next) => {
        const resolved = typeof next === "function" ? next(valueRef.current) : next;
        valueRef.current = resolved;
        setValue(resolved);
        onChange?.handler?.(resolved);
    }, [onChange]);

    // Close on an outside mousedown or Escape — but never on the opening gesture.
    useEffect(() => {
        if (!menuOpened) return;
        const onDocMouseDown = (e) => {
            // The opening gesture is already excluded: the listener attaches on the
            // next tick (setTimeout 0), and the 150ms window guards re-entrancy.
            if (Date.now() - openedAtRef.current < 150) return;
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
                closeContextMenu();
            }
        };
        const onKey = (e) => { if (e.key === "Escape") closeContextMenu(); };
        const id = setTimeout(() => {
            document.addEventListener("mousedown", onDocMouseDown);
            document.addEventListener("keydown", onKey);
        }, 0);
        return () => {
            clearTimeout(id);
            document.removeEventListener("mousedown", onDocMouseDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [menuOpened, closeContextMenu]);

    return (
        <ContextMenuContext.Provider value={{
            isOpen: menuOpened,
            openContextMenu,
            closeContextMenu,
            toggleContextMenu,
            contextMenuRef,
        }}>
            {children}
            <ContextMenu
                ref={contextMenuRef}
                isOpen={menuOpened}
                setMenuOpened={closeContextMenu}
                position={position}
                fullText={fullText}
                selectedText={selectedText}
                selectionStartEnd={selectionStartEnd}
                inputRef={inputRef}
                value={value}
                setValue={applyValue}
                network={network}
            />
        </ContextMenuContext.Provider>
    );
};

export { ContextMenuContext, ContextMenuProvider };
